"use client";

import { createContext, useCallback, useContext, useMemo } from "react";
import { useAuth } from "@/context/AuthContext";
import { usePortal } from "@/context/PortalContext";
import { useRegisterPush } from "@/hooks/useRegisterPush";
import { isNativeApp } from "@/lib/nativePlatform";

const PushNotificationsContext = createContext(null);

export function PushNotificationsProvider({ children }) {
  const { user } = useAuth();
  const portal = usePortal();
  const surface = useMemo(
    () => ({
      portalId: portal?.id || "",
      revisionId: portal?.revisionId || "",
      origin: typeof window !== "undefined" ? window.location.origin : "",
      notificationIconPath: portal?.browserBranding?.notificationIconPath || "",
    }),
    [portal?.browserBranding?.notificationIconPath, portal?.id, portal?.revisionId]
  );
  const showForegroundNotification = useCallback(async (payload) => {
    if (
      typeof window === "undefined" ||
      typeof Notification === "undefined" ||
      Notification.permission !== "granted" ||
      !("serviceWorker" in navigator)
    ) {
      return;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      const notification = payload?.notification || {};
      await registration.showNotification(notification.title || "New activity", {
        body: notification.body || "",
        icon:
          payload?.data?.notificationIcon ||
          notification.icon ||
          portal?.browserBranding?.notificationIconPath ||
          "/icons/icon-192.png",
        badge: notification.badge || "/icons/badge.png",
        data: { url: payload?.data?.url || window.location.href },
      });
    } catch (error) {
      console.warn("[push] foreground notification display failed", error);
    }
  }, [portal?.browserBranding?.notificationIconPath]);
  const push = useRegisterPush({
    user,
    vapidKey: process.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY,
    disabled: isNativeApp(),
    surface,
    onForegroundMessage: showForegroundNotification,
  });

  return (
    <PushNotificationsContext.Provider value={push}>
      {children}
    </PushNotificationsContext.Provider>
  );
}

export function usePushNotifications() {
  const value = useContext(PushNotificationsContext);
  if (!value) {
    throw new Error("usePushNotifications must be used within PushNotificationsProvider.");
  }
  return value;
}
